import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, Users, Award, ArrowLeft } from "lucide-react";

// Hero slides
const slides = [
  {
    id: 1,
    image_url: "https://imgg.io/images/2025/11/23/c449fc35ae7c9ff1f9bccf12acfa989e.jpg",
    headline: "زان ... حيث تُصنع الهوية ويُروى الأثر",
    subheading: "نبني للعلامة التجارية حضورًا يُرى، يُحَس، يُذكر",
    cta_text: "ابدأ بناء علامتك التجارية",
    isH1: true
  },
  {
    id: 2,
    image_url: "https://imgg.io/images/2025/11/23/8506939fcea3ff6e878c50f034586d69.jpg",
    headline: "زان ... نروي قصتك بطريقة تُلهم",
    subheading: "نحول الفكرة إلى قصة تُلهم .. من الفكرة إلى الأثر",
    cta_text: "دعنا نبني قصتك",
    isH1: false
  },
  {
    id: 3,
    image_url: "https://imgg.io/images/2025/12/02/df7d19a78a03dd0ca5be93a03d7f3499.png",
    headline: "زان ... حيث تبدأ القصة",
    subheading: "لأن لكل فكرة حكاية .. زان هو البداية",
    cta_text: "أطلق مشروعك بثقة",
    isH1: false
  }
];

// Trust indicators
const trustIndicators = [
  { icon: Users, text: "أكثـر من 2000 عميـل", color: "text-white" },
  { icon: Award, text: "مُنذ عام 2012", color: "text-white" },
  { icon: MapPin, text: "المملكة العربية السعـودية", color: "text-white" }
];



export default function Hero() {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const goToSlide = (index) => setCurrentIndex(index);

  return (
    <section className="border-b-4 border-white font-cairo relative min-h-screen w-full flex flex-col items-center justify-center overflow-hidden">

      {/* Background Images */}
      <AnimatePresence initial={false}>
        <motion.div
          key={currentIndex}
          className="absolute inset-0 w-full h-full"
          initial={{ opacity: 0, scale: 1.1 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.5, ease: "easeInOut" }}
        >
          <img
            src={slides[currentIndex].image_url}
            alt={slides[currentIndex].headline}
            className="w-full h-full object-cover object-center"
          />
        </motion.div>
      </AnimatePresence>

      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent z-10" />

      {/* Content */}
      <div className="relative z-20 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 sm:pt-36 md:pt-40 pb-16 sm:pb-20">
        <div className="w-full max-w-6xl">
          <motion.div
            key={currentIndex}
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -30 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="text-white space-y-6 sm:space-y-8"
          >
            {slides[currentIndex].isH1 ? (
              <h1 className="text-[clamp(1rem,4vw,2.5rem)] font-medium leading-[1.1]">
                {slides[currentIndex].headline}
                <br />
<span className="block text-[#141d3c] enhanced-glow-text mt-4 sm:mt-6 text-[0.85em]">
                    {slides[currentIndex].subheading}
                </span>
              </h1>
            ) : (
              <h2 className="text-[clamp(1rem,4vw,2.5rem)] font-medium leading-[1.1]">
                {slides[currentIndex].headline}
                <br />
                <span className="block text-[#141d3c] enhanced-glow-text mt-4 sm:mt-6 text-[0.85em]">
                  {slides[currentIndex].subheading}
                </span>
              </h2>
            )}

            {/* Trust Indicators */}
            <div className="flex flex-wrap gap-4 sm:gap-6 pt-2 sm:pt-4">
              {trustIndicators.map((item, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <item.icon className={`w-4 h-4 sm:w-5 sm:h-5 ${item.color}`} />
                  <span className="text-xs sm:text-sm font-medium text-white/90">{item.text}</span>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 pt-4 sm:pt-6">
              <button className="bg-gradient-to-r from-[#141d3c] to-[#e8e9eb] text-white px-6 py-4 rounded-full font-semibold hover:scale-105 transition-all duration-500 flex items-center justify-center gap-2">
                {slides[currentIndex].cta_text} <ArrowLeft />
              </button>
              <button
                onClick={() => window.location.href = 'tel:+966579379779'}
                className="bg-white/10 backdrop-blur-sm text-white border-2 border-white/30 px-6 py-4 rounded-full font-semibold hover:bg-white/30 hover:text-[#141d3c] transition-all duration-500 flex items-center justify-center gap-2"
              >
                تـواصل معـنا
              </button>
            </div>
          </motion.div>
        </div>
      </div>


      {/* Navigation Dots */}
      <div className="absolute bottom-24 left-1/2 -translate-x-1/2 flex gap-2 z-30">
        {slides.map((_, idx) => (
          <button
            key={idx}
            onClick={() => goToSlide(idx)}
            className={`w-2 h-2 sm:w-3 sm:h-3 rounded-full ${
              currentIndex === idx ? "bg-white scale-125" : "bg-white/50 hover:bg-white"
            } transition-all duration-300`}
          />
        ))}
      </div>

    </section>
  );
}